import { Form, Formik } from "formik";
import React, { useState, useEffect } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  updateBuildApi,
  getLatestBuildApi,
} from "../../../services/buildUpload.service";
import { toast } from "react-toastify";
import { Switch } from "antd";
import InputText from "../../../components/common/InputText";
import LoaderMu from "../../../components/common/LoaderMu";

const BuildAdd = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [buildId, setBuildId] = useState(null);
  const [initialData, setInitialData] = useState(null);

  // FETCH BUILD DATA ON MOUNT
  useEffect(() => {
    fetchBuildData();
  }, []);

  const fetchBuildData = () => {
    setLoading(true);
    // Try to get build data - using ANDROID as default, but we need the build ID
    getLatestBuildApi("ANDROID")
      .then((response) => {
        const buildData = response?.data;
        if (buildData?.id) {
          setBuildId(buildData.id);
          setInitialData({
            androidVersion: buildData?.androidVersion?.toString() || "",
            iosVersion: buildData?.iosVersion?.toString() || "",
            RequredUpdateIOS: buildData?.requiredUpdateIos || false,
            RequredUpdateAndroid: buildData?.requiredUpdateAndroid || false,
            allowCheck: buildData?.allowCheck || false,
          });
        } else {
          // If no ID found, set default ID to 1 (as per the curl example)
          setBuildId(1);
          setInitialData({
            androidVersion: "",
            iosVersion: "",
            RequredUpdateIOS: false,
            RequredUpdateAndroid: false,
            allowCheck: false,
          });
        }
      })
      .catch((error) => {
        // If API fails, set default ID to 1
        setBuildId(1);
        setInitialData({
          androidVersion: "",
          iosVersion: "",
          RequredUpdateIOS: false,
          RequredUpdateAndroid: false,
          allowCheck: false,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // HANDLE FORM SUBMIT
  const handleSubmit = (values) => {
    if (!buildId) {
      toast.error("Build ID not found. Please refresh the page.");
      return;
    }

    setLoading(true);

    // Prepare JSON payload matching API requirements
    const payload = {
      androidVersion: values?.androidVersion || "",
      iosVersion: values?.iosVersion || "",
      requiredUpdateAndroid: values?.RequredUpdateAndroid || false,
      requiredUpdateIos: values?.RequredUpdateIOS || false,
      allowCheck: values?.allowCheck || false,
    };

    updateBuildApi(buildId, payload)
      .then((response) => {
        toast.success(
          response?.data?.message ?? "Build settings updated successfully."
        );
      })
      .catch((error) => {
        if (error?.status === 500) {
          toast.error(
            "Unauthorized or server issue. Please refresh or try later."
          );
        } else {
          toast.error(
            error?.error_description
              ? error?.error_description
              : error?.details
              ? error?.details
              : error?.message
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (!initialData) {
    return (
      <div className="d-flex flex-column pageContainer p-3 overflow-auto overflow-x-hidden">
        <LoaderMu loading={true} />
      </div>
    );
  }

  return (
    <div className="d-flex flex-column pageContainer p-3 overflow-auto overflow-x-hidden">
      <LoaderMu loading={loading} />
      <div className="flex-grow-1 pageContent position-relative pt-4">
        <Card className="h-100 bg-white shadow border-0 theme-card-cover">
          <div className="theme-card-header px-1">
            <Stack
              direction="horizontal"
              gap={2}
              className="flex-wrap justify-content-between px-3 pt-3 pb-2 align-items-start"
            >
              <h5 className="mb-0 position-relative fw-medium">
                Build Settings
              </h5>
            </Stack>
          </div>
          <div className="flex-grow-1 d-flex flex-column px-1">
            <div className="p-3 d-flex flex-column h-100">
              <Formik
                enableReinitialize
                initialValues={initialData}
                onSubmit={(values, actions) => {
                  actions.setSubmitting(false);
                  handleSubmit(values, actions);
                }}
              >
                {({
                  handleSubmit,
                  handleChange,
                  handleBlur,
                  values,
                  setFieldValue,
                  touched,
                  errors,
                }) => (
                  <Form className="d-flex flex-column flex-grow-1 theme-from">
                    <Row className="gx-xl-5">
                      <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                        <InputText
                          type="text"
                          controlId="androidVersion"
                          label="Android Version"
                          value={values?.androidVersion}
                          name="androidVersion"
                          maxLength="70"
                          errorsField={errors?.androidVersion}
                          touched={touched?.androidVersion}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          className={"w-100 form-control"}
                          variant="outlined"
                        />
                        <div className="mt-3">
                          <label className="mb-2 d-block">
                            Required Update Android
                          </label>
                          <Switch
                            id="RequredUpdateAndroid"
                            checked={values?.RequredUpdateAndroid}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            className="fw-bold"
                            onChange={(value) => {
                              setFieldValue("RequredUpdateAndroid", value);
                            }}
                          />
                        </div>
                      </Col>
                      <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                        <InputText
                          type="text"
                          controlId="iosVersion"
                          label="iOS Version"
                          value={values?.iosVersion}
                          name="iosVersion"
                          maxLength="70"
                          errorsField={errors?.iosVersion}
                          touched={touched?.iosVersion}
                          handleChange={handleChange}
                          handleBlur={handleBlur}
                          className={"w-100 form-control"}
                          variant="outlined"
                        />
                        <div className="mt-3">
                          <label className="mb-2 d-block">
                            Required Update iOS
                          </label>
                          <Switch
                            id="RequredUpdateIOS"
                            checked={values?.RequredUpdateIOS}
                            checkedChildren="Yes"
                            unCheckedChildren="No"
                            className="fw-bold"
                            onChange={(value) => {
                              setFieldValue("RequredUpdateIOS", value);
                            }}
                          />
                        </div>
                      </Col>
                    </Row>
                    <Row className="gx-xl-5">
                      <Col sm={6} md={6} lg={6} className="mb-4 pb-1">
                        <label className="mb-2 d-block">Allow Check</label>
                        <Switch
                          id="allowCheck"
                          checked={values?.allowCheck}
                          checkedChildren="Yes"
                          unCheckedChildren="No"
                          className="fw-bold"
                          onChange={(value) => {
                            setFieldValue("allowCheck", value);
                          }}
                        />
                      </Col>
                    </Row>
                    <Stack direction="horizontal" gap={3} className="my-3">
                      <Button
                        variant="primary"
                        onClick={handleSubmit}
                        className="rounded-xs fs-15"
                        type="submit"
                        disabled={loading}
                      >
                        Save
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          navigate(-1);
                        }}
                        className="rounded-xs fs-15"
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default BuildAdd;
